import React from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { version } from '../../package.json'

export function getTemplateFailureMessage (failure, locale) {
  const codeMessages = {
    DESTINATION_FOLDER_UNAVAILABLE: locale.template_error_destination_folder,
    INVALID_DOCUMENT_EXTENSION: locale.template_error_extension,
    PDS_DATA_CELL_MISSING: locale.template_error_pds_cell,
    PDS_DATA_VISIBILITY: locale.template_error_pds_visibility
  }
  if (codeMessages[failure.code]) return codeMessages[failure.code]
  if (failure.status === 401) return locale.template_error_authentication
  if (failure.status === 403) return locale.template_error_permission
  if (failure.status === 404) return locale.template_error_not_found
  if (failure.status === 409) return locale.template_error_conflict
  if (failure.status === 429) return locale.template_error_throttled
  if (failure.status >= 500) return locale.template_error_server
  return locale[`template_error_${failure.stage}`] || locale.template_error_unknown
}

export function getTemplateRunFeedback (report, locale) {
  if (!report) return null
  if (!report.running) return ''
  if (!report.isBatch) return null
  const phase = locale[`template_phase_${report.phase}`]
  if (report.phase === 'reading') return phase
  const folders = report.phase === 'folders'
  const processed = folders ? report.foldersProcessed : report.processed
  const total = folders ? report.foldersTotal : report.total
  const label = folders ? locale.template_run_folders : locale.template_run_processed
  return `${phase}\n${label}: ${processed}/${total}`
}

export function formatTemplateRunReport (report, locale, createdAt = new Date()) {
  const documentFailures = report.failures.filter(failure => failure.kind === 'template' || failure.kind === 'file').length
  const title = report.running ? locale.template_run_running : report.failures.length || !report.sourceComplete ? locale.template_run_failed : locale.template_run_done
  const lines = [
    locale.template_run_support_title,
    `${locale.template_run_generated}: ${createdAt.toISOString()}`,
    `${locale.template_run_version}: ${version}`,
    `${locale.template_run_status}: ${title}`,
    '',
    `${locale.template_run_processed}: ${report.processed}/${report.total}`,
    `${locale.template_run_saved}: ${report.succeeded}`,
    `${locale.template_run_document_errors}: ${documentFailures}`,
    `${locale.template_run_other_errors}: ${report.failures.length - documentFailures}`,
    `${locale.template_run_folders}: ${report.foldersProcessed}/${report.foldersTotal}`,
    report.sourceComplete ? locale.template_run_source_complete : locale.template_run_incomplete,
    '',
    `${locale.template_run_errors} (${report.failures.length})`
  ]

  for (const [index, failure] of report.failures.entries()) {
    lines.push('', `${index + 1}. ${failure.name || failure.path}`)
    if (failure.path) lines.push(`${locale.template_run_path}: ${failure.path}`)
    if (failure.destination) lines.push(`${locale.template_run_destination}: ${failure.destination}`)
    lines.push(`${locale[`template_stage_${failure.stage}`] || locale.template_stage_processing}: ${getTemplateFailureMessage(failure, locale)}`)
    if (failure.status) lines.push(`HTTP: ${failure.status}`)
    if (failure.code) lines.push(`${locale.template_run_error_code}: ${failure.code}`)
    if (failure.message) lines.push(`${locale.template_run_technical}: ${failure.message}`)
  }

  lines.push('', locale.template_run_support_context, '')
  return lines.join('\n')
}

export async function copyTemplateRunReport (text, container) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return
    }
  } catch {}

  const field = document.createElement('textarea')
  const activeElement = document.activeElement
  const parent = container || document.body
  field.value = text
  field.setAttribute('readonly', '')
  field.style.position = 'fixed'
  field.style.opacity = '0'
  parent.appendChild(field)
  try {
    field.focus()
    field.select()
    if (!document.execCommand('copy')) throw new Error('Clipboard copy was blocked')
  } finally {
    field.remove()
    activeElement?.focus()
  }
}

export default function TemplateRunReport ({ report, locale, onClose }) {
  const [copyResult, setCopyResult] = React.useState(null)
  const contentRef = React.useRef(null)
  if (!report || report.running || (!report.failures.length && report.sourceComplete)) return null

  const copyStatus = copyResult?.report === report ? copyResult.status : null
  const copyReport = async () => {
    const text = formatTemplateRunReport(report, locale)
    setCopyResult({ report, status: 'copying' })
    try {
      await copyTemplateRunReport(text, contentRef.current)
      setCopyResult({ report, status: 'success' })
    } catch {
      setCopyResult({ report, status: 'error', text })
      contentRef.current?.scrollTo({ top: 0 })
    }
  }
  const { total, succeeded, failures, sourceComplete } = report

  return (
    <Dialog
      open
      fullWidth
      maxWidth='sm'
      aria-labelledby='template-run-title'
      aria-describedby='template-run-summary'
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown') onClose()
      }}
      slotProps={{ paper: { sx: { m: 2, width: 'calc(100% - 32px)', borderRadius: 1, letterSpacing: 0 } } }}
    >
      <DialogTitle component='div' sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Typography id='template-run-title' component='h2' sx={{ flex: 1, fontSize: 20, fontWeight: 600, overflowWrap: 'anywhere' }}>
          {locale.template_run_failed}
        </Typography>
        <Tooltip title={locale.close}>
          <span>
            <IconButton aria-label={locale.close} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent ref={contentRef} dividers sx={{ maxHeight: '65vh', overflowY: 'auto', overflowWrap: 'anywhere' }}>
        {copyStatus === 'error' && (
          <Box sx={{ mb: 2 }}>
            <Alert severity='error' sx={{ mb: 2 }}>{locale.template_run_copy_error}</Alert>
            <TextField
              label={locale.template_run_support_title}
              value={copyResult.text}
              multiline
              rows={8}
              fullWidth
              slotProps={{ input: { readOnly: true } }}
              onFocus={event => event.target.select()}
            />
          </Box>
        )}
        <Box id='template-run-summary' role='status' aria-live='polite'>
          <Typography variant='body2'>
            {locale.template_run_saved}: <strong>{succeeded}/{total}</strong>
          </Typography>
        </Box>

        {!sourceComplete && <Alert severity='warning' sx={{ mt: 2 }}>{locale.template_run_incomplete}</Alert>}

        {failures.length > 0 && (
          <Box component='section' aria-labelledby='template-run-errors' sx={{ mt: 3 }}>
            <Typography id='template-run-errors' component='h3' sx={{ fontSize: 16, fontWeight: 600 }}>
              {locale.template_run_errors} ({failures.length})
            </Typography>
            <Box component='ol' sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {failures.map((failure, index) => (
                <Box component='li' key={`${failure.kind}:${failure.path}:${index}`} sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>{failure.name || locale.template_run_errors}</Typography>
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    {getTemplateFailureMessage(failure, locale)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions disableSpacing sx={{ px: 3, py: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto auto' }, justifyContent: { sm: 'end' }, gap: 1 }}>
        {copyStatus && copyStatus !== 'copying' && (
          <Typography role='status' aria-live='polite' variant='body2' sx={{ gridColumn: '1 / -1', color: copyStatus === 'error' ? 'error.main' : 'text.secondary' }}>
            {copyStatus === 'success' ? locale.template_run_copy_success : locale.template_run_copy_error}
          </Typography>
        )}
        <Button onClick={copyReport} disabled={copyStatus === 'copying'} variant='outlined' startIcon={<ContentCopyIcon />}>
          {locale.template_run_copy}
        </Button>
        <Button onClick={onClose} variant='outlined' startIcon={<CloseIcon />}>{locale.close}</Button>
      </DialogActions>
    </Dialog>
  )
}
